import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import Panel from "../../components/Panel";
import useInventoryForecast from "../useInventoryForecast";
import useVenueDayRollover from "../useVenueDayRollover";
import { clampRange, defaultRange, formatDayShort } from "../forecastRange";

const CARD_STYLE = {
  boxShadow: "none",
  border: `1px solid ${T.border}`,
  borderRadius: 12,
  height: 560,
  boxSizing: "border-box",
  overflowY: "auto",
};

const useStyles = makeStyles({
  panel: { padding: "16px 20px 14px", display: "flex", flexDirection: "column", gap: 12 },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  eyebrow: { fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: T.subtleFg, fontWeight: 600 },
  dates: { display: "flex", alignItems: "center", gap: 6 },
  dateInput: { width: 122, borderRadius: 6, border: `1px solid ${T.border}`, background: T.elevated, color: T.fg, fontSize: 12, fontFamily: "inherit", padding: "5px 7px" },
  separator: { color: T.subtleFg, fontSize: 12 },
  status: { color: T.mutedFg, fontSize: 12, padding: "8px 0" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 13 },
  th: { textAlign: "left", fontSize: 11, color: T.subtleFg, fontWeight: 500, padding: "2px 8px 6px", position: "sticky", top: -16, background: T.elevated, zIndex: 1 },
  row: { "& td": { padding: "9px 8px", color: T.fg, borderTop: `1px solid ${T.borderSoft}` } },
  count: { fontWeight: 600, fontVariantNumeric: "tabular-nums" },
  vip: { color: T.primary, fontSize: 11, marginLeft: 6, fontWeight: 600 },
});

function vipArrivalCount(day) {
  return (day.movements ?? []).filter(
    (movement) => movement.kind === "ARRIVAL" && movement.isVip,
  ).length;
}

export default function OperationsSchedulePanel({ timezone }) {
  const s = useStyles();
  const [range, setRange] = useState(() => defaultRange(timezone));

  useEffect(() => {
    setRange(defaultRange(timezone));
  }, [timezone]);

  useVenueDayRollover(timezone, (currentDay, previousDay) => {
    setRange((current) => {
      if (current.startDate !== previousDay) return current;

      const next = clampRange({
        startDate: currentDay,
        endDate: current.endDate,
        timezone,
        edited: "startDate",
      });
      return { startDate: next.startDate, endDate: next.endDate };
    });
  });

  const { days, loading, error } = useInventoryForecast({
    lotId: null,
    startDate: range.startDate,
    endDate: range.endDate,
  });

  const applyRange = (next, edited) => {
    const result = clampRange({ ...next, timezone, edited });
    setRange({ startDate: result.startDate, endDate: result.endDate });
  };

  return (
    <Panel className={s.panel} style={CARD_STYLE} aria-label="Schedule">
      <div className={s.head}>
        <span className={s.eyebrow}>Schedule</span>
        <div className={s.dates} aria-label="Schedule date range">
          <input type="date" className={s.dateInput} value={range.startDate} aria-label="Schedule start date" onChange={(event) => applyRange({ ...range, startDate: event.target.value }, "startDate")} />
          <span className={s.separator}>to</span>
          <input type="date" className={s.dateInput} value={range.endDate} aria-label="Schedule end date" onChange={(event) => applyRange({ ...range, endDate: event.target.value }, "endDate")} />
        </div>
      </div>

      {loading && <div className={s.status}>Loading schedule…</div>}
      {error && <div className={s.status}>Could not load schedule.</div>}
      {!loading && !error && days.length === 0 && <div className={s.status}>No forecast data in this date range.</div>}

      {days.length > 0 && (
        <table className={s.table} aria-label="Inventory forecast schedule">
          <thead><tr><th className={s.th}>Date</th><th className={s.th}>Arrivals</th><th className={s.th}>Departures</th></tr></thead>
          <tbody>
            {days.map((day) => {
              const vip = vipArrivalCount(day);
              return (
                <tr key={day.date} className={s.row}>
                  <td>{formatDayShort(day.date)}</td>
                  <td><span className={s.count}>{day.arrivals}</span>{vip > 0 && <span className={s.vip}>{vip} VIP</span>}</td>
                  <td className={s.count}>{day.departures}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
